
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { MessageSquare } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Club } from '@/types/clubs';
import ClubHeader from '@/components/clubs/ClubHeader';
import ClubSidebar from '@/components/clubs/ClubSidebar';
import ClubAboutTab from '@/components/clubs/ClubAboutTab';
import ClubDiscussionThreads from '@/components/clubs/ClubDiscussionThreads';
import ClubRestrictedContent from '@/components/clubs/ClubRestrictedContent';
import ClubLoading from '@/components/clubs/ClubLoading';
import ClubNotFound from '@/components/clubs/ClubNotFound';
import ThreadView from '@/components/clubs/threads/ThreadView';

const ClubDetails = () => {
  const { clubId, threadId } = useParams<{ clubId: string; threadId?: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);
  const [joiningClub, setJoiningClub] = useState(false);
  const [activeTab, setActiveTab] = useState('about');

  // Detect if we're viewing a thread
  const isViewingThread = location.pathname.includes('/threads/');

  useEffect(() => {
    if (isViewingThread) {
      setActiveTab('discussions');
    }
  }, [isViewingThread]);

  useEffect(() => {
    const fetchClubDetails = async () => {
      if (!clubId) return;
      
      try {
        setLoading(true);
        
        // Fetch club details
        const { data: clubData, error } = await supabase
          .from('clubs')
          .select('*')
          .eq('id', clubId)
          .single();
          
        if (error) throw error;
        
        // Check if user is a member
        if (user) {
          const { data: memberData } = await supabase
            .from('club_members')
            .select('*')
            .eq('club_id', clubId)
            .eq('user_id', user.id)
            .single();
            
          if (clubData) {
            setClub({
              ...clubData,
              is_member: !!memberData
            });
          }
        } else {
          setClub(clubData);
        }
      } catch (error) {
        console.error('Error fetching club details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClubDetails();
  }, [clubId, user]);

  const handleJoinClub = async () => {
    if (!user || !clubId || !club) return;
    
    try {
      setJoiningClub(true);
      
      // Check if already a member
      const { data: existingMember } = await supabase
        .from('club_members')
        .select('*')
        .eq('club_id', clubId)
        .eq('user_id', user.id)
        .single();
        
      // If already a member, leave the club
      if (existingMember) {
        const { error } = await supabase
          .from('club_members')
          .delete()
          .eq('club_id', clubId)
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        setClub({ ...club, is_member: false });
      } 
      // Otherwise join the club
      else {
        const { error } = await supabase
          .from('club_members')
          .insert({
            club_id: clubId,
            user_id: user.id,
            joined_at: new Date().toISOString()
          });
          
        if (error) throw error;
        
        setClub({ ...club, is_member: true });
      }
    } catch (error) {
      console.error('Error joining/leaving club:', error);
    } finally {
      setJoiningClub(false);
    }
  };

  if (loading) {
    return <ClubLoading />;
  }

  if (!club) {
    return <ClubNotFound />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <NavBar />
      <div className="container mx-auto p-4 flex-1">
        <ClubHeader
          club={club}
          joiningClub={joiningClub}
          onJoinClub={handleJoinClub}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Club Details */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden mb-6">
              <CardContent className="p-6">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="about">About</TabsTrigger>
                    <TabsTrigger value="discussions">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Discussions
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="about" className="mt-0">
                    <ClubAboutTab description={club.description || ''} />
                  </TabsContent>
                  
                  <TabsContent value="discussions" className="mt-0">
                    {club.is_member ? (
                      isViewingThread && threadId ? (
                        <ThreadView />
                      ) : (
                        <ClubDiscussionThreads clubId={club.id} />
                      )
                    ) : (
                      <ClubRestrictedContent 
                        onJoinClub={handleJoinClub}
                        joiningClub={joiningClub}
                      />
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
          
          {/* Sidebar */}
          <div>
            <ClubSidebar club={club} />
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ClubDetails;
